import { publicConfig } from "@/PublicConfig";
import { PageMetadata } from "@/utils/page";
import NextHead from "next/head";
import { useRouter } from "next/router";

// See: https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/markup

type NextHeadProps = Partial<Parameters<typeof NextHead>[0]>;

interface HeadProps extends NextHeadProps {
  article?: {
    modifiedTime?: string;
    publishedTime?: string;
  };
  canonicalUrl?: string;
  locales: any;
  metadata?: PageMetadata;
  ogType?: "website" | "article";
  openGraph?: {
    imageUrl?: string;
  };
  page: any;
  twitter?: {
    image?: string;
  };
}

const Head = ({
  article,
  canonicalUrl: providedCanonicalUrl,
  locales,
  metadata,
  ogType = "website",
  openGraph,
  page,
  twitter,
  ...props
}: HeadProps) => {
  const router = useRouter();

  const head = page?.attributes?.Head;

  const canonicalUrl = new URL(publicConfig.visualpingWebURL);

  const pathname = router.asPath.split(/[?#]/, 1)[0] || "/";
  let newPathname = page?.attributes?.locale === "en" ? pathname : `/${page?.attributes?.locale}${pathname}`;

  if (newPathname.endsWith("/")) {
    newPathname = newPathname.slice(0, -1);
  }

  canonicalUrl.pathname = newPathname;
  const resolvedCanonicalUrl = providedCanonicalUrl ?? canonicalUrl.toString();
  const ogImageUrl = head?.OpenGraphImageUrl || openGraph?.imageUrl;

  return (
    <NextHead {...props}>
      <title>{head?.Title}</title>

      <meta name="generate-date" content={metadata?.generateDate ?? "unknown"} />
      <meta name="version" content={metadata?.version ?? "unknown"} />

      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      <link rel="canonical" href={resolvedCanonicalUrl} />

      <meta name="category" content={head?.Category} />
      <meta name="description" content={head?.Description} />
      <meta name="keywords" content={head?.Keywords} />

      <meta property="og:description" content={head?.OpenGraphDescription || head?.Description} />
      {/* Plain og:image is the property most scrapers read; og:image:url alone is skipped by some. */}
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      {ogImageUrl && <meta property="og:image:url" content={ogImageUrl} />}
      <meta property="og:image:alt" content={head?.OpenGraphImageAlt || "Visualping"} />
      <meta property="og:site_name" content={head?.OpenGraphSiteName || "Visualping"} />
      <meta property="og:title" content={head?.OpenGraphTitle || head?.Title} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={resolvedCanonicalUrl} />

      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:description" content={head?.TwitterDescription || head?.Description} />
      <meta
        name="twitter:image"
        content={head?.TwitterImage || head?.OpenGraphImageUrl || twitter?.image || openGraph?.imageUrl}
      />
      <meta name="twitter:image:alt" content={head?.TwitterImageAlt || head?.OpenGraphImageAlt || "Visualping"} />
      <meta name="twitter:site" content={head?.TwitterSite || "@visualping"} />
      <meta name="twitter:title" content={head?.TwitterTitle || head?.Title} />

      <link rel="icon" href={head?.Favicon.data?.attributes?.url} />
      <link rel="shortcut icon" href={head?.Favicon?.data?.attributes?.url} />

      <meta name="robots" content={head?.Robots || publicConfig.robots} />
    </NextHead>
  );
};

export default Head;
